import { createAction, createSlice } from '@reduxjs/toolkit';
import {
  fetchBills,
  fetchContractNumberCheckboxesValues,
  fetchStatusCheckboxesValues,
} from './thunks';
import { Bill } from '../../types/Bill';

export interface Filter {
  column: string;
  filters: ColumnFilters;
}

export interface ColumnFilters {
  dateFrom?: Date;
  dateTo?: Date;
  search?: string;
  checkbox?: FilterCheckbox[];
}

export interface FilterCheckbox {
  value?: string;
  checked?: boolean;
}

export interface BillState {
  bills?: Bill[];
  totalPageCount?: number;
  totalBillCount?: number;
  loadingBills?: boolean;
  loadingBillsError?: string;
  checkedBills: string[];
  billFilters: Filter[];
  filterCheckboxOptions: Filter[];
}

const initialState: BillState = {
  bills: undefined,
  totalPageCount: 1,
  totalBillCount: 0,
  loadingBills: true,
  loadingBillsError: undefined,
  checkedBills: [],
  billFilters: [],
  filterCheckboxOptions: [],
};

export const setCheckedBills = createAction<string[]>('setCheckedBills');

export const setColumnFilter = createAction<Filter>('setColumnFilter');

export const deleteBillColumnFilters = createAction<string>(
  'deleteBillColumnFilters',
);

export const setFilterCheckboxState = createAction<{
  column: string;
  checkbox: string;
  value: boolean;
}>('setFilterCheckboxState');

export const billsSlice = createSlice({
  name: 'bills',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchBills.pending, (state) => {
      state.loadingBills = initialState.loadingBills;
      state.loadingBillsError = initialState.loadingBillsError;
      state.bills = initialState.bills;
      state.totalPageCount = initialState.totalPageCount;
      state.totalBillCount = initialState.totalBillCount;
      state.checkedBills = initialState.checkedBills;
    });
    builder.addCase(fetchBills.fulfilled, (state, action) => {
      state.bills = action.payload.bills;
      state.totalPageCount = action.payload.totalPageCount;
      state.totalBillCount = action.payload.totalBills;
      state.loadingBills = false;
      state.checkedBills = [];
    });
    builder.addCase(fetchBills.rejected, (state, action) => {
      state.loadingBills = false;
      state.loadingBillsError = action.payload
        ? (action.payload as Error)?.message
        : action.error?.message;
    });
    builder.addCase(setCheckedBills, (state, action) => {
      state.checkedBills = action.payload;
    });
    builder.addCase(setColumnFilter, (state, action) => {
      state.billFilters =
        state.billFilters.findIndex(
          (filter) => filter.column === action.payload.column,
        ) > -1
          ? state.billFilters.map((filter) => {
              if (filter.column === action.payload.column) {
                return {
                  column: filter.column,
                  filters: action.payload.filters,
                };
              }

              return filter;
            })
          : [...state.billFilters, action.payload];
    });
    builder.addCase(deleteBillColumnFilters, (state, action) => {
      state.billFilters = state.billFilters.filter(
        (filter) => filter.column !== action.payload,
      );
      state.filterCheckboxOptions = state.filterCheckboxOptions.map(
        (filter) => {
          if (filter.column === action.payload) {
            filter.filters.checkbox = filter.filters.checkbox?.map(
              (checkbox) => ({
                ...checkbox,
                checked: false,
              }),
            );
          }
          return filter;
        },
      );
    });
    builder.addCase(fetchStatusCheckboxesValues.fulfilled, (state, action) => {
      const checkboxFromFilter = action.payload.map((checkbox: string) => ({
        value: checkbox,
        checked: false,
      }));

      state.filterCheckboxOptions =
        state.filterCheckboxOptions.findIndex(
          (filter) => filter.column.toLowerCase() === 'status',
        ) > -1
          ? (state.filterCheckboxOptions = state.filterCheckboxOptions.map(
              (filter) => {
                if (filter.column.toLowerCase() === 'status') {
                  filter.filters.checkbox = checkboxFromFilter;
                }
                return filter;
              },
            ))
          : [
              ...state.filterCheckboxOptions,
              {
                column: 'Status',
                filters: {
                  checkbox: checkboxFromFilter,
                },
              },
            ];
    });
    builder.addCase(
      fetchContractNumberCheckboxesValues.fulfilled,
      (state, action) => {
        const checkboxFromFilter = action.payload.map((checkbox: string) => ({
          value: checkbox,
          checked: false,
        }));

        state.filterCheckboxOptions =
          state.filterCheckboxOptions.findIndex(
            (filter) => filter.column.toLowerCase() === 'broj ugovora',
          ) > -1
            ? (state.filterCheckboxOptions = state.filterCheckboxOptions.map(
                (filter) => {
                  if (filter.column.toLowerCase() === 'broj ugovora') {
                    filter.filters.checkbox = checkboxFromFilter;
                  }
                  return filter;
                },
              ))
            : [
                ...state.filterCheckboxOptions,
                {
                  column: 'Broj ugovora',
                  filters: {
                    checkbox: checkboxFromFilter,
                  },
                },
              ];
      },
    );
    builder.addCase(setFilterCheckboxState, (state, action) => {
      state.filterCheckboxOptions = state.filterCheckboxOptions.map(
        (checkbox) => {
          if (checkbox.column === action.payload.column) {
            checkbox.filters.checkbox?.map((checkboxColumn) => {
              if (checkboxColumn.value === action.payload.checkbox) {
                checkboxColumn.checked = action.payload.value;
              }
              return checkboxColumn;
            });
          }
          return checkbox;
        },
      );
    });
  },
});

export default billsSlice.reducer;
